import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Button, IconButton } from "./ui";

export default function PdfPreviewModal({ url, title = "PDF preview", onClose }) {
  const dialogRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!url) return undefined;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const frame = window.requestAnimationFrame(() => dialogRef.current?.querySelector("button")?.focus());
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeRef.current();
      }
      if (event.key !== "Tab") return;
      const focusable = dialogRef.current?.querySelectorAll('a[href], button:not([disabled]), iframe');
      if (!focusable?.length) return;
      if (event.shiftKey && document.activeElement === focusable[0]) {
        event.preventDefault();
        focusable[focusable.length - 1].focus();
      } else if (!event.shiftKey && document.activeElement === focusable[focusable.length - 1]) {
        event.preventDefault();
        focusable[0].focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus?.();
    };
  }, [url]);

  if (!url) return null;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 p-3 backdrop-blur-sm sm:p-6"
      role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section ref={dialogRef} role="dialog" aria-modal="true" aria-label={title}
        className="flex h-[min(90dvh,960px)] w-full max-w-5xl flex-col overflow-hidden rounded-dialog border border-default bg-surface-raised text-foreground shadow-dialog">
        <div className="flex items-center justify-between gap-3 border-b border-subtle px-4 py-3 sm:px-5">
          <h2 className="min-w-0 truncate text-section-title">{title}</h2>
          <div className="flex shrink-0 items-center gap-2">
            <Button as="a" href={url} target="_blank" rel="noopener noreferrer" size="sm">Open in new tab</Button>
            <IconButton icon={X} label={`Close ${title}`} size="sm" onClick={onClose} />
          </div>
        </div>
        <iframe title={title} src={url} className="min-h-0 w-full flex-1 bg-white" />
      </section>
    </div>, document.body
  );
}
